<template>
  <PageLoading v-if="loading" />
  <ErrorMessage v-else-if="error" />
  <V1ScreenScaffold
    v-else
    :title="t('superpacman-other-amount-title')"
    title-align="center"
    :next-disabled="!isValid"
    data-testid="v1-other-amount"
    @next="handleContinue"
  >
    <div class="v1-other-amount">
      <div class="v1-other-amount__amount">
        <span class="v1-other-amount__symbol">{{ currencySymbol }}</span>
        <!-- No native <input>: the amount is plain text driven by the keypad, so
             the device keyboard never opens in the webview. -->
        <span class="v1-other-amount__value" data-testid="v1-other-amount-value">{{ displayAmount }}</span>
      </div>

      <p v-if="allowsCustomAmount && minDisplay" class="v1-other-amount__min" data-testid="v1-other-amount-hint">
        {{ t("superpacman-minimum-amount") }} {{ minDisplay }}
      </p>

      <p
        class="v1-other-amount__badge"
        :class="{ 'v1-other-amount__badge--hidden': !nextPaymentMessage }"
        data-testid="v1-other-amount-next-payment"
      >
        {{ nextPaymentMessage || "" }}
      </p>

      <div
        class="v1-other-amount__chips"
        :class="allowsCustomAmount ? 'v1-other-amount__chips--scroll' : 'v1-other-amount__chips--grid'"
        data-testid="v1-other-amount-chips"
      >
        <V1AmountChip
          v-for="chip in chips"
          :key="`${chip.days}-${chip.amount.value}`"
          :label="chip.term"
          :selected="selectedDays === chip.days"
          :data-testid="`v1-other-amount-chip-${chip.days}`"
          @select="selectChip(chip)"
        />
      </div>

      <V1NumericKeypad
        v-if="allowsCustomAmount"
        class="v1-other-amount__keypad"
        :allows-decimals="meta.allowsDecimals"
        :decimal-separator="meta.decimalSeparator"
        data-testid="v1-other-amount-keypad"
        @press="onKeypadPress"
      />
    </div>
  </V1ScreenScaffold>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import V1NumericKeypad from "@/components/v1/V1NumericKeypad.vue";
import V1AmountChip from "@/components/v1/V1AmountChip.vue";
import PageLoading from "@/components/PageLoading.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import { getFallbackLocale, updateCurrencySymbol } from "@/i18n";
import {
  applyKeypadKey,
  CurrencyMeta,
  currencyToNumber,
  deriveCurrencyMeta,
  deriveCurrencySymbol,
  formatSanitizedAmount,
  sanitizeAmount,
} from "@/shared-helpers/currencyAmount.helper";
import { fetchV1PaymentInfo } from "@/helpers/v1TestHelper";
import { IV1Installment, IV1PaymentInfo } from "@/types/IV1PaymentInfo";

const MAX_INPUT_LENGTH = 14;

/**
 * v1 custom-amount entry (PYMT-2287), rebuilt in the new UI. The amount is entered
 * with an in-webview numeric keypad (`V1NumericKeypad`) — never a native input —
 * so the device keyboard stays closed. Formatting and validation reuse the shared
 * `currencyAmount.helper`, whose behaviour is driven by the app's own i18n currency
 * formatter after `updateCurrencySymbol` configures it for the device country,
 * making the output identical to the legacy other-amount screen for every market.
 * Server-provided installment terms render as chips; the "next payment" date is
 * bucket-picked from `installments[].next_payment`.
 */
export default defineComponent({
  name: "V1OtherAmountView",
  components: { V1ScreenScaffold, V1NumericKeypad, V1AmountChip, PageLoading, ErrorMessage },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const i18n = useI18n();
    const t = i18n.t;
    const paymentInfo = ref<IV1PaymentInfo | null>(null);
    const loading = ref<boolean>(true);
    const error = ref<boolean>(false);
    // The user-typed amount as a string (digits + at most one decimal separator).
    const raw = ref<string>("");
    const selectedDays = ref<number | null>(null);

    const allowsCustomAmount = computed<boolean>(() => Boolean(paymentInfo.value?.allowsCustomAmount));
    const chips = computed<IV1Installment[]>(() => paymentInfo.value?.installments ?? []);
    const minDisplay = computed<string>(() => paymentInfo.value?.minimumPaymentAmount?.display || "");

    // Format amounts with the currency locale for the device COUNTRY (e.g. BR →
    // pt-BR), not the app's ?lang locale — the webview may be launched with a
    // different language, which would otherwise show the wrong decimal separator.
    const currencyLocale = computed<string>(() => getFallbackLocale(paymentInfo.value?.country || i18n.locale.value));
    const formatCurrency = (value: number): string => i18n.n(value, "currency", currencyLocale.value);
    const meta = computed<CurrencyMeta>(() => deriveCurrencyMeta(formatCurrency));
    const currencySymbol = computed<string>(() => deriveCurrencySymbol(formatCurrency) || paymentInfo.value?.currency || "");

    const sanitizedResult = computed(() => sanitizeAmount(raw.value, meta.value));
    const amountValue = computed<number>(() => currencyToNumber(sanitizedResult.value.sanitized, meta.value.decimalSeparator));

    const displayAmount = computed<string>(() => {
      if (!raw.value) return "0";
      if (sanitizedResult.value.pending) return sanitizedResult.value.sanitized;
      const { display } = formatSanitizedAmount(sanitizedResult.value.sanitized, meta.value, formatCurrency, currencySymbol.value);
      return display || "0";
    });

    const minAmount = computed<number>(() => Number(paymentInfo.value?.minimumPaymentAmount?.value) || 0);
    const maxAmount = computed<number>(() => Number(paymentInfo.value?.maximumPaymentAmount?.value) || Infinity);

    const isValid = computed<boolean>(() => {
      const value = amountValue.value;
      if (!Number.isFinite(value) || value <= 0) return false;
      return value >= minAmount.value && value <= maxAmount.value;
    });

    const selectedChip = computed<IV1Installment | undefined>(() =>
      chips.value.find((c) => c.days === selectedDays.value),
    );

    const nextPaymentMessage = computed<string | null>(() => {
      // The backend pre-composes the ready-to-render line into
      // `next_payment.display` (advance: prefix + date; reduce-interest: the
      // constant interest-savings sentence). We just pick the right row.
      if (selectedChip.value?.nextPayment?.display) return selectedChip.value.nextPayment.display;
      let picked: IV1Installment | null = null;
      for (const row of chips.value) {
        if (row.days === 0 || !row.nextPayment?.display) continue;
        if (amountValue.value >= Number(row.amount.value)) {
          if (!picked || Number(row.amount.value) >= Number(picked.amount.value)) picked = row;
        }
      }
      return picked?.nextPayment?.display ?? null;
    });

    // Convert a plain numeric string ("178.23") to a typed-raw string in the
    // locale's decimal separator so the keypad can keep editing it.
    const numberToRaw = (numericStr: string): string => {
      if (!numericStr.includes(".")) return numericStr;
      const [intPart, decPart] = numericStr.split(".");
      return meta.value.allowsDecimals && decPart ? `${intPart}${meta.value.decimalSeparator}${decPart}` : intPart;
    };

    const selectChip = (chip: IV1Installment): void => {
      selectedDays.value = chip.days;
      raw.value = numberToRaw(chip.amount.value);
    };

    const onKeypadPress = (key: string): void => {
      selectedDays.value = null;
      raw.value = applyKeypadKey(raw.value, key, meta.value, MAX_INPUT_LENGTH);
    };

    onMounted(async () => {
      try {
        paymentInfo.value = await fetchV1PaymentInfo();
        // Configure the i18n currency format for the device country (mirrors the
        // legacy InstallmentsView) so the shared helper formats like production.
        if (paymentInfo.value?.country) {
          updateCurrencySymbol(currencyLocale.value, i18n.numberFormats.value, paymentInfo.value.country);
        }
      } catch (fetchError) {
        console.error("[v1] Failed to fetch payment-info:", fetchError);
        error.value = true;
      } finally {
        loading.value = false;
      }
    });

    const handleContinue = (): void => {
      if (!isValid.value) return;
      // A chip is a known installment (custom=false); a free-typed value is custom.
      if (selectedDays.value !== null && selectedChip.value) {
        router.push({
          name: "V1ProvidersByDevice",
          params: { deviceTag: route.params.deviceTag },
          query: { amount: selectedChip.value.amount.value },
        });
        return;
      }
      router.push({
        name: "V1ProvidersByDevice",
        params: { deviceTag: route.params.deviceTag },
        query: { amount: String(amountValue.value), custom: "true" },
      });
    };

    return {
      t,
      loading,
      error,
      selectedDays,
      allowsCustomAmount,
      chips,
      minDisplay,
      currencySymbol,
      meta,
      displayAmount,
      isValid,
      nextPaymentMessage,
      selectChip,
      onKeypadPress,
      handleContinue,
    };
  },
});
</script>

<style scoped>
.v1-other-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 56px;
  /* Fill the scaffold body so the chips + keypad cluster can sit at the bottom. */
  flex: 1 1 auto;
}

.v1-other-amount__amount {
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--pj-color-brand-gray-850, #222926);
  font-family: "Inter", sans-serif;
  font-weight: 600;
  letter-spacing: -0.5px;
}

.v1-other-amount__symbol {
  font-size: 32px;
  line-height: 40px;
}

.v1-other-amount__value {
  font-size: 57px;
  line-height: 64px;
  font-family: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  color: inherit;
}

.v1-other-amount__min {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  line-height: 14px;
  color: var(--pj-color-brand-gray-550, #74756f);
}

.v1-other-amount__badge {
  /* Reserve the badge's height even when empty so showing a date doesn't shift
     the chips/keypad below it (paired with visibility:hidden, not v-if). */
  box-sizing: border-box;
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 24px 0 0;
  padding: 4px 12px;
  border-radius: var(--pj-radius-pill, 999px);
  background: var(--pj-color-brand-gray-175, #e8e8e3);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.v1-other-amount__badge--hidden {
  visibility: hidden;
}

.v1-other-amount__chips {
  box-sizing: border-box;
  align-self: stretch;
  /* Push the chips + keypad cluster to the bottom of the screen (Figma 1206:14105). */
  margin-top: auto;
  padding: 16px;
}

.v1-other-amount__chips--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.v1-other-amount__chips--scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.v1-other-amount__chips--scroll .v1-chip {
  flex-shrink: 0;
}

.v1-other-amount__keypad {
  align-self: stretch;
  margin-top: 16px;
}
</style>
